import styled, { css } from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import { orbitTokens } from '../../styles';
import { PlainButton, tint } from '../components/pageStyles';

// Colours of the standalone frames (6566:9267); most carry an Orbit token's hex.
export const wz = {
  page: orbitTokens.surfaceConsole,
  panel: orbitTokens.surfaceAlt,
  field: orbitTokens.surface,
  fieldEnd: '#0A1020',
  border: orbitTokens.border,
  line: orbitTokens.borderWidth,
  text: orbitTokens.text,
  secondary: orbitTokens.textSecondary,
  // Figma's #64709A is 3.62:1 on the panel; Orbit's lifted token clears AA (4.57:1).
  muted: orbitTokens.textMuted,
  placeholder: orbitTokens.placeholder,
  accent: orbitTokens.action,
  violet: orbitTokens.accent,
  success: orbitTokens.positive,
  highlight: '#F5A623',
  danger: orbitTokens.danger
} as const;

/** Figma's own sizes and line heights; the family stays Orbit's (the frames are Inter). */
export const wzType = {
  hero: 'font-size: 34px; line-height: 40.8px; letter-spacing: -1.02px;',
  title: 'font-size: 25px; line-height: 30.4px; letter-spacing: -0.38px;',
  question: 'font-size: 15px; line-height: 18.4px;',
  tile: 'font-size: 16px; line-height: 21.6px;',
  body: 'font-size: 14.5px; line-height: 17.6px;',
  input: 'font-size: 14px; line-height: 16.8px;',
  control: 'font-size: 13.5px; line-height: 16px;',
  label: 'font-size: 13px; line-height: 16px;',
  hint: 'font-size: 12.5px; line-height: 15.2px;',
  eyebrow: 'font-size: 11.5px; line-height: 13.6px; letter-spacing: 1.15px;'
} as const;

export const FIELD_WIDTH = { sm: 120, md: 190, lg: 300, xl: 460 } as const;

export type FieldWidth = keyof typeof FIELD_WIDTH;

export const gradientPrimary = `linear-gradient(90deg, ${wz.violet} 0%, ${wz.accent} 100%)`;

const fieldBox = css`
  background: ${wz.field};
  border: ${wz.line} solid ${wz.border};
  border-radius: 8px;
  box-shadow: none;
  color: ${wz.text};
  ${wzType.input}
`;

/**
 * The dark skin of the antd inputs, selects, date pickers and their popups. The wizard re-homes
 * every popup into this root (`ConfigProvider getPopupContainer`), so the skin reaches them too.
 */
export const WizardRoot = styled.div`
  position: relative;
  color: ${wz.text};

  .ant-input,
  .ant-input-number,
  .ant-picker,
  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    ${fieldBox}
  }

  .ant-input,
  .ant-input-number-input,
  .ant-picker-input > input {
    color: ${wz.text};
    ${wzType.input}
  }

  .ant-input,
  .ant-input-number-input {
    height: 34.8px;
    padding: 9px 14px;
  }

  .ant-input-number {
    width: 100%;
  }

  .ant-input-number-handler-wrap {
    display: none;
  }

  .ant-picker {
    height: 36.4px;
    padding: 0 14px;
  }

  .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 36.4px;
    padding: 0 34px 0 14px;

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 34.8px;
    }
  }

  .ant-input::placeholder,
  .ant-input-number-input::placeholder,
  .ant-picker-input > input::placeholder,
  .ant-select-selection-placeholder {
    color: ${wz.placeholder};
  }

  .ant-input:focus,
  .ant-input-number-focused,
  .ant-picker-focused,
  .ant-select-focused:not(.ant-select-disabled) .ant-select-selector,
  .ant-select-open .ant-select-selector {
    border-color: ${wz.accent} !important;
    box-shadow: none !important;
  }

  .ant-select-arrow,
  .ant-picker-suffix,
  .ant-picker-clear {
    color: ${wz.muted};
    background: transparent;
  }

  .ant-input[disabled],
  .ant-input-number-disabled,
  .ant-picker-disabled,
  .ant-select-disabled .ant-select-selector {
    background: ${wz.field} !important;
    color: ${wz.secondary} !important;
  }

  .ant-select-dropdown,
  .ant-picker-dropdown .ant-picker-panel-container {
    padding: 6px;
    background: ${wz.panel};
    border: ${wz.line} solid ${wz.accent};
    border-radius: 10px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  }

  .ant-select-item {
    padding: 10px 13px;
    border-radius: 7px;
    color: ${wz.secondary};
    ${wzType.input}
  }

  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background: ${tint(wz.accent, 0.08)};
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: ${tint(wz.accent, 0.14)};
    color: ${wz.accent};
    font-weight: 600;
  }

  .ant-picker-panel,
  .ant-picker-header,
  .ant-picker-content th,
  .ant-picker-cell,
  .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner,
  .ant-picker-header button {
    color: ${wz.secondary};
    background: transparent;
    border-color: ${wz.border};
  }

  .ant-picker-cell-in-view {
    color: ${wz.text};
  }

  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-selected
    .ant-picker-time-panel-cell-inner {
    background: ${wz.accent};
    color: ${wz.page};
  }

  .ant-picker-cell:hover:not(.ant-picker-cell-selected) .ant-picker-cell-inner,
  .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell
    .ant-picker-time-panel-cell-inner:hover {
    background: ${tint(wz.accent, 0.14)};
  }

  .ant-picker-footer,
  .ant-picker-ranges,
  .ant-picker-time-panel-column:not(:first-child) {
    border-color: ${wz.border};
  }

  .ant-picker-ok .ant-btn {
    background: ${wz.accent};
    border-color: ${wz.accent};
  }

  .ant-picker-now-btn,
  .ant-picker-today-btn {
    color: ${wz.accent};
  }

  .ant-slider-rail {
    background: ${wz.border};
  }

  .ant-slider-track {
    background: ${wz.accent};
  }

  .ant-slider-handle {
    background: ${wz.text};
    border-color: ${wz.accent};
  }

  /* Every popup re-homed here scrolls on the AI screens' scrollbar, never the OS one: select and
     picker lists, the picker modals' wrap and tables. */
  .rc-virtual-list-holder,
  .ant-select-dropdown .rc-virtual-list-holder-inner,
  .ant-picker-time-panel-column,
  .ant-modal-wrap,
  .ant-modal-body,
  .ant-table-body,
  .ant-table-content {
    ${intelligenceScrollbar('both')}
  }
`;

export const fieldWidth = (width: FieldWidth) => css`
  width: ${FIELD_WIDTH[width]}px;
  flex: none;
`;

export const LinkButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  padding: 4px 2px;
  color: ${wz.secondary};
  font-weight: 600;
  ${wzType.control}

  > * + * {
    margin-left: 5px;
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40.6px;
  padding: 12px 22px;
  border-radius: 9px;
  font-weight: 700;
  ${wzType.input}
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  border: ${wz.line} solid transparent;
  background: ${gradientPrimary};
  color: #ffffff;
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  border: ${wz.line} solid ${wz.border};
  background: ${wz.field};
  color: ${wz.text};
`;

export { tint };
